/**
 * AgendaTodayCard — daily schedule card shown on the admin dashboard.
 *
 * Appears below Infraestrutura. When there are no items for the day it
 * stays compact with a single "+ Novo" button + the AI planner. When
 * there ARE items, each one shows a cycling status chip (afio → feito →
 * reagendado → finalizado), can be rescheduled to another day, and the
 * consolidated materials shopping list is rendered below the agenda.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CalendarDays,
  Plus,
  Sparkles,
  Clock,
  AlertCircle,
  CheckCircle2,
  RefreshCcw,
  Flag,
  ShoppingBag,
  Trash2,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Loader2,
  Bell,
  BellOff,
  Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import AgendaItemFormDialog from "@/components/AgendaItemFormDialog";
import AgendaRescheduleDialog from "@/components/AgendaRescheduleDialog";
import AgendaAIPlanDialog from "@/components/AgendaAIPlanDialog";
import AgendaCalendarDialog from "@/components/AgendaCalendarDialog";
import useAgendaReminder from "@/hooks/useAgendaReminder";

const todayIso = () => new Date().toISOString().slice(0, 10);

const STATUS = {
  afio:        { label: "A fazer",     Icon: Clock,         tint: "bg-slate-100 text-slate-700 border-slate-200" },
  feito:       { label: "Feito",       Icon: CheckCircle2,  tint: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  reagendado:  { label: "Reagendado",  Icon: RefreshCcw,    tint: "bg-amber-100 text-amber-800 border-amber-200" },
  finalizado:  { label: "Finalizado",  Icon: Flag,          tint: "bg-indigo-100 text-indigo-800 border-indigo-200" },
};
const NEXT_STATUS = {
  afio: "feito",
  feito: "finalizado",
  reagendado: "afio",
  finalizado: "afio",
};

const TYPE_LABEL = {
  agendamento: "Agendamento",
  visita_orcamento: "Visita de orçamento",
  lembrete: "Lembrete",
};

const PRIORITY = {
  baixa:  { label: "Baixa",  tint: "bg-slate-50 text-slate-600" },
  normal: { label: "Normal", tint: "bg-sky-50 text-sky-700" },
  alta:   { label: "Alta",   tint: "bg-rose-100 text-rose-700" },
};

const materialLines = (txt) => (txt || "").split(/\n|;/).map((s) => s.trim()).filter(Boolean);

const AgendaItemDetails = ({ item, onEdit, onReschedule, onRemove }) => {
  const mats = materialLines(item.materials);
  return (
    <div className="mt-2 space-y-2 text-[12px] text-slate-700 bg-slate-50/80 rounded-lg p-2.5" data-testid={`agenda-details-${item.id}`}>
      <div>
        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Assunto</p>
        <p className="font-semibold text-slate-900">{item.title || "(sem título)"}</p>
        <p className="text-[10.5px] text-slate-500">
          {TYPE_LABEL[item.type] || item.type}
          {item.school_name && <> · {item.school_name}</>}
          {item.budget_number && <> · Orç. Nº {item.budget_number}</>}
          {item.priority && item.priority !== "normal" && <> · Prioridade {PRIORITY[item.priority]?.label}</>}
        </p>
      </div>
      <div>
        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Descrição</p>
        <p className="whitespace-pre-wrap">{item.observation || <span className="italic text-slate-400">Sem descrição</span>}</p>
      </div>
      <div>
        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Materiais</p>
        {mats.length ? (
          <ul className="mt-0.5 space-y-0.5" data-testid={`agenda-details-materials-${item.id}`}>
            {mats.map((m, i) => (
              <li key={i} className="flex items-start gap-1.5"><ShoppingBag className="w-3 h-3 text-amber-600 mt-0.5 shrink-0" />{m}</li>
            ))}
          </ul>
        ) : <p className="italic text-slate-400">Nenhum material</p>}
      </div>
      <div className="flex flex-wrap gap-1.5 pt-1">
        <button type="button" onClick={() => onEdit(item)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold text-white bg-violet-600 hover:bg-violet-700" data-testid={`agenda-edit-${item.id}`}>
          <Pencil className="w-3 h-3" /> Editar
        </button>
        <button type="button" onClick={() => onReschedule(item)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200" data-testid={`agenda-reschedule-${item.id}`}>
          <RefreshCcw className="w-3 h-3" /> Reagendar
        </button>
        <button type="button" onClick={() => onRemove(item.id)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200" data-testid={`agenda-delete-${item.id}`}>
          <Trash2 className="w-3 h-3" /> Remover
        </button>
        {item.ai_generated && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 font-semibold text-[10px]"><Sparkles className="w-2.5 h-2.5" /> IA</span>
        )}
      </div>
      {(item.history || []).length > 1 && (
        <details className="text-[10.5px] text-slate-500">
          <summary className="cursor-pointer font-semibold">Histórico ({item.history.length})</summary>
          <ul className="mt-1 ml-3 space-y-0.5">
            {item.history.slice().reverse().map((h, idx) => (
              <li key={idx}>• {new Date(h.at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} — {h.msg}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default function AgendaTodayCard({ hideIfEmpty = false } = {}) {
  const reminder = useAgendaReminder();
  const [items, setItems] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [upcoming, setUpcoming] = useState([]);     // next days when today is empty
  const [loading, setLoading] = useState(true);
  const [calOpen, setCalOpen] = useState(false);    // monthly calendar modal
  const [formOpen, setFormOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rescheduling, setRescheduling] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [confirmDone, setConfirmDone] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, m, u] = await Promise.all([
        api.get(`/agenda?date=${todayIso()}`),
        api.get(`/agenda/materials-today?date=${todayIso()}`),
        api.get(`/agenda/upcoming?limit=5`),
      ]);
      setItems(a.data?.items || []);
      setMaterials(m.data?.items || []);
      setUpcoming(u.data?.items || []);
    } catch {
      setItems([]);
      setMaterials([]);
      setUpcoming([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleExpand = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const cycleStatus = async (item, confirmed = false) => {
    const next = NEXT_STATUS[item.status || "afio"];
    if (next === "feito" && !confirmed) { setConfirmDone(item); return; }
    try {
      await api.patch(`/agenda/${item.id}`, { status: next });
      setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, status: next } : x)));
      toast.success(`Marcado como ${STATUS[next].label}`);
      // Materials "done" overlay follows status
      if (next === "feito" || next === "afio") load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao mudar status");
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Remover este item do cronograma?")) return;
    try {
      await api.delete(`/agenda/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      setUpcoming((prev) => prev.filter((x) => x.id !== id));
      toast.success("Item removido");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover");
    }
  };

  const openEdit = (item) => { setEditing(item); setFormOpen(true); };
  const openCreate = () => { setEditing(null); setFormOpen(true); };

  const toggleMaterial = async (m, idx) => {
    setMaterials((list) => list.map((x, i) => (i === idx ? { ...x, done: !x.done } : x)));
    try {
      await api.post(`/agenda/${m.agenda_item_id}/material-toggle`, { label: m.label });
    } catch { load(); }
  };

  const stats = useMemo(() => {
    const base = { afio: 0, feito: 0, reagendado: 0, finalizado: 0 };
    for (const i of items) base[i.status || "afio"] = (base[i.status || "afio"] || 0) + 1;
    return base;
  }, [items]);
  const total = items.length;
  const visibleItems = useMemo(() => items.filter((i) => !["feito", "finalizado"].includes(i.status)), [items]);

  if (loading && !items.length) {
    // When used on the Dashboard with hideIfEmpty, we don't want a loading
    // skeleton either — the card should remain fully invisible until it
    // confirms there's content to show.
    if (hideIfEmpty) return null;
    return (
      <div className="rounded-2xl bg-white border border-slate-100 card-shadow p-4 flex items-center gap-3">
        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        <span className="text-xs text-slate-500">Carregando cronograma de hoje…</span>
      </div>
    );
  }

  // Dashboard variant (hideIfEmpty=true): the user still wants to see
  // "Cronograma diário" when today is empty, as long as there's SOMETHING
  // upcoming — so we only disappear entirely when there's nothing today,
  // no pending materials AND nothing scheduled ahead.
  if (hideIfEmpty && items.length === 0 && materials.length === 0 && upcoming.length === 0) {
    return null;
  }

  return (
    <>
      <section
        className="rounded-2xl bg-white border border-slate-100 card-shadow overflow-hidden"
        data-testid="agenda-today-card"
      >
        {/* Header */}
        {/* Header — compact: smaller icon/title and icon-only action buttons */}
        <div className="p-3 pb-2 border-b border-slate-100 flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-fuchsia-500 to-violet-700 text-white flex items-center justify-center shadow-md shrink-0">
            <CalendarDays className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-black text-[#1e3a8a] text-[14px] leading-tight">Cronograma de hoje</h3>
            <p className="text-[10.5px] text-slate-500 leading-tight">
              {new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
              {total > 0 && (
                <>
                  {" · "}{total} item{total !== 1 && "s"}
                  {stats.feito > 0 && <> · {stats.feito} feito{stats.feito !== 1 && "s"}</>}
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {reminder.supported && (
              <button
                type="button"
                onClick={() => (reminder.enabled ? reminder.disable() : reminder.requestEnable())}
                className={`w-7 h-7 rounded-md border flex items-center justify-center ${
                  reminder.enabled
                    ? "border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                    : "border-slate-300 text-slate-500 hover:bg-slate-50"
                }`}
                data-testid="btn-agenda-reminder"
                title={reminder.enabled ? "Desativar lembretes (30 min antes)" : "Ativar lembretes (30 min antes)"}
                aria-label="Lembretes"
                aria-pressed={reminder.enabled}
              >
                {reminder.enabled ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
              </button>
            )}
            <button
              type="button"
              onClick={() => setCalOpen(true)}
              className="w-7 h-7 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center justify-center"
              data-testid="btn-agenda-calendar"
              title="Abrir calendário"
              aria-label="Abrir calendário"
            >
              <CalendarDays className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setAiOpen(true)}
              className="w-7 h-7 rounded-md border border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50 flex items-center justify-center"
              data-testid="btn-agenda-ai"
              title="Montar meu dia com IA"
              aria-label="Montar meu dia com IA"
            >
              <Sparkles className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="w-7 h-7 rounded-md bg-gradient-to-r from-fuchsia-600 to-violet-700 hover:from-fuchsia-700 hover:to-violet-800 text-white flex items-center justify-center"
              data-testid="btn-agenda-new"
              title="Novo item"
              aria-label="Novo item"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Items list */}
        {visibleItems.length === 0 ? (
          <div className="px-4 py-5">
            {upcoming.length > 0 ? (
              <div data-testid="agenda-upcoming">
                <p className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2">
                  {items.length > 0 ? "Tudo feito hoje · próximos" : "Nada hoje · próximos"}
                </p>
                <ul className="divide-y divide-slate-100 rounded-xl border border-slate-100">
                  {upcoming.slice(0, 4).map((u) => {
                    const dt = new Date((u.date || "") + "T12:00:00");
                    const dLabel = dt.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" });
                    const uOpen = expanded.has(u.id);
                    return (
                      <li key={u.id} className="p-3" data-testid={`agenda-upcoming-${u.id}`}>
                        <button type="button" onClick={() => toggleExpand(u.id)} className="w-full flex items-start gap-3 text-left" data-testid={`agenda-upcoming-toggle-${u.id}`}>
                          <div className="w-12 shrink-0 text-center">
                            <span className="inline-block px-1.5 py-0.5 rounded-md bg-fuchsia-50 text-fuchsia-700 text-[10px] font-black">
                              {dLabel}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className={`text-[13px] font-black text-slate-900 leading-tight ${uOpen ? "" : "truncate"}`}>
                              {u.title || "(sem título)"}
                            </p>
                            <p className="text-[10.5px] text-slate-500 leading-tight truncate">
                              {u.time ? `${u.time} • ` : ""}{u.budget_number ? `Nº ${u.budget_number}` : (TYPE_LABEL[u.type] || "")}
                            </p>
                          </div>
                          {uOpen ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0 mt-1" />}
                        </button>
                        {uOpen && <AgendaItemDetails item={u} onEdit={openEdit} onReschedule={setRescheduling} onRemove={removeItem} />}
                      </li>
                    );
                  })}
                </ul>
                <button
                  type="button"
                  onClick={() => setCalOpen(true)}
                  className="mt-3 w-full text-[11.5px] font-bold text-fuchsia-700 hover:underline"
                  data-testid="agenda-view-all"
                >
                  Ver calendário completo →
                </button>
              </div>
            ) : (
              <div className="py-4 text-center">
                <CalendarDays className="w-10 h-10 text-slate-200 mx-auto" />
                <p className="mt-2 text-sm text-slate-500">Nada agendado.</p>
                <p className="text-[11px] text-slate-400">Toque em <strong>Novo</strong> pra criar ou <strong>IA</strong> pra sugerir seu dia.</p>
              </div>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {visibleItems.map((item) => {
              const st = STATUS[item.status || "afio"];
              const StIcon = st.Icon;
              const isOpen = expanded.has(item.id);
              return (
                <li key={item.id} className="p-2.5" data-testid={`agenda-item-${item.id}`}>
                  <div className="flex items-center gap-2">
                    {/* Time pill — only when set, otherwise the slot vanishes */}
                    {item.time && (
                      <span className="px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-700 text-[10.5px] font-black shrink-0">
                        {item.time}
                      </span>
                    )}
                    {/* Title — tap expands details (edit only via button) */}
                    <button
                      type="button"
                      onClick={() => toggleExpand(item.id)}
                      className="flex-1 min-w-0 text-left hover:opacity-80"
                      data-testid={`agenda-item-title-${item.id}`}
                    >
                      <p className={`font-heading font-black text-[13px] text-slate-900 leading-tight ${isOpen ? "" : "truncate"}`}>
                        {item.title || "(sem título)"}
                      </p>
                      <p className="text-[10px] text-slate-500 leading-tight truncate">
                        <span className="font-semibold text-fuchsia-700">{TYPE_LABEL[item.type] || item.type}</span>
                        {item.budget_number && <> · Nº {item.budget_number}</>}
                        {item.priority && item.priority !== "normal" && (
                          <> · <span className="font-bold text-rose-600">{PRIORITY[item.priority].label}</span></>
                        )}
                      </p>
                    </button>
                    {/* Status chip — tap to cycle */}
                    <button
                      type="button"
                      onClick={() => cycleStatus(item)}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border shrink-0 ${st.tint}`}
                      data-testid={`agenda-status-${item.id}`}
                      title="Toque pra avançar o status"
                    >
                      <StIcon className="w-2.5 h-2.5" />
                      {st.label}
                    </button>
                    {/* Expand (only when there's extra content) */}
                    <button
                      type="button"
                      onClick={() => toggleExpand(item.id)}
                      className="w-6 h-6 shrink-0 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-400"
                      data-testid={`agenda-expand-${item.id}`}
                      aria-label="Expandir"
                    >
                      {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>
                  </div>

                  {isOpen && <AgendaItemDetails item={item} onEdit={openEdit} onReschedule={setRescheduling} onRemove={removeItem} />}
                </li>
              );
            })}
          </ul>
        )}

        {/* Consolidated materials — collapsible to keep the widget slim */}
        {materials.length > 0 && (
          <details className="border-t-2 border-dashed border-slate-200 bg-amber-50/60 group">
            <summary className="list-none cursor-pointer px-3 py-2 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-amber-700 shrink-0" />
              <h4 className="font-heading font-black text-[12.5px] text-amber-900 flex-1 min-w-0 truncate">
                Material pra comprar/trazer ({materials.length})
              </h4>
              <ChevronDown className="w-4 h-4 text-amber-700 shrink-0 transition-transform group-open:rotate-180" />
            </summary>
            <ul className="space-y-2.5 px-3 pb-3 pt-1" data-testid="agenda-materials-list">
              {materials.map((m, idx) => (
                <li key={`${m.agenda_item_id}-${idx}`} className="flex items-center gap-3 text-[13px]">
                  <button
                    type="button"
                    onClick={() => toggleMaterial(m, idx)}
                    className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                      m.done
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : "border-amber-300 hover:border-amber-500 bg-white"
                    }`}
                    data-testid={`material-check-${idx}`}
                  >
                    {m.done && <CheckCircle2 className="w-4 h-4" strokeWidth={3} />}
                  </button>
                  <div className={m.done ? "line-through text-slate-400" : "text-slate-800"}>
                    <span className="font-semibold">{m.label}</span>
                    {m.school && <span className="text-slate-500"> · {m.school}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      <AgendaItemFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        onSaved={() => { setFormOpen(false); load(); }}
      />
      <AgendaRescheduleDialog
        item={rescheduling}
        onClose={() => setRescheduling(null)}
        onDone={() => { setRescheduling(null); load(); }}
      />
      <AgendaAIPlanDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        onApplied={() => { setAiOpen(false); load(); }}
      />
      <AgendaCalendarDialog
        open={calOpen}
        onOpenChange={setCalOpen}
        onChanged={load}
      />
      <AlertDialog open={!!confirmDone} onOpenChange={(o) => !o && setConfirmDone(null)}>
        <AlertDialogContent data-testid="agenda-done-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Finalizar este item?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDone?.title || "(sem título)"}" será marcado como feito e sairá da tela. Ele continua no calendário.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="agenda-done-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const it = confirmDone; setConfirmDone(null); cycleStatus(it, true); }}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="agenda-done-confirm-btn"
            >
              Sim, finalizar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
